import { api } from './api';
import { authApi } from './authApi';
import { PageContextSummary, OpportunityType, GA4ConnectionStatus } from '../types/ga4.types';

export interface GA4Property {
  propertyId: string;
  displayName: string;
  url?: string;
  permissionLevel?: string;
}

export interface GetOpportunitiesParams {
  tenantId: number | string;
  type?: OpportunityType;
  windowDays?: number;
  limit?: number;
}

export const ga4ContextService = {
  /**
   * Get page context summary with SEO + GA4 business value metrics
   */
  async getPageContext(
    pageId: number,
    windowDays: number = 28
  ): Promise<PageContextSummary> {
    const response = await api.get(`/pages/${pageId}/context`, {
      params: { windowDays },
    });
    return response.data;
  },

  /**
   * Get list of business opportunities sorted by estimated ROI
   * Uses GA4 Context endpoint: /api/v1/tenants/{tenantId}/opportunities
   * 
   * IMPORTANT: This is different from /api/v1/opportunities (OCI-based technical opportunities)
   * This endpoint returns PageContextSummary with GA4 business metrics + opportunity classification
   */
  async getOpportunities(
    filters: GetOpportunitiesParams
  ): Promise<PageContextSummary[]> {
    const { tenantId, type, windowDays = 28, limit = 50 } = filters;
    
    console.log('🔍 Fetching GA4 opportunities:', {
      endpoint: `/tenants/${tenantId}/opportunities`,
      params: { opportunityType: type, windowDays, limit }
    });
    
    try {
      const response = await api.get(`/tenants/${tenantId}/opportunities`, {
        params: {
          opportunityType: type, // Backend param name is 'opportunityType', not 'type'
          windowDays,
          limit,
        },
      });
      console.log('✅ GA4 opportunities response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch GA4 opportunities:', error);
      throw error;
    }
  },

  /**
   * Link GA4 property to tenant with full configuration
   */
  async linkGA4Property(
    propertyId: string,
    tenantId: number,
    engagementMetrics: string[],
    stripQueryStrings: boolean
  ): Promise<void> {
    await api.post('/integrations/ga4/link', {
      propertyId,
      tenantId,
      engagementMetrics,
      stripQueryStrings,
    });
  },

  /**
   * Get GA4 connection status for tenant
   */
  async getGA4Status(tenantId: number): Promise<GA4ConnectionStatus> {
    const response = await api.get(`/integrations/ga4/oauth/status/${tenantId}`);
    return response.data;
  },

  /**
   * Trigger manual GA4 data sync
   */
  async triggerGA4Sync(tenantId: number): Promise<void> {
    await api.post('/integrations/ga4/sync', {
      tenantId,
    });
  },

  /**
   * Initiate GA4 OAuth flow - calls authentication-framework (port 8084)
   */
  async initiateGA4OAuth(siteId: number, propertyId?: string): Promise<{ authorizationUrl: string }> {
    // Use authApi to call authentication-framework on port 8084
    const response = await authApi.get('/oauth/ga4/authorize', {
      params: { siteId }
    });
    // Backend returns { authorizationUrl, state }
    return response.data;
  },

  /**
   * Handle GA4 OAuth callback
   */
  async handleGA4OAuthCallback(
    code: string,
    state: string
  ): Promise<{ success: boolean; propertyId?: string }> {
    console.log('📤 Calling /oauth/callback endpoint');
    console.log('Code:', code?.substring(0, 20) + '...');
    console.log('State:', state);
    
    const response = await api.post('/integrations/ga4/oauth/callback', {
      code,
      state,
    });
    
    console.log('✅ /oauth/callback response:', response.data);
    return response.data;
  },

  /**
   * List available GA4 properties for a site after OAuth connection
   */
  async listGA4Properties(siteId: number): Promise<GA4Property[]> {
    console.log('📤 Fetching GA4 properties for siteId:', siteId);
    const response = await api.get(`/oauth/ga4/properties/${siteId}`);
    console.log('✅ GA4 properties response:', response.data);
    // Backend wraps in ApiResponse { data: [...] }
    return response.data.data || response.data || [];
  },
};
